import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { FaChevronLeft } from "react-icons/fa";
import ProviderPaymentsTab from "../../components/ProviderPaymentsTab";
import { markSectionVisited } from "../../utils/providerOnboarding";
import "../../components/ProviderDetails.css";
import classes from "./PaymentsAcceptedOnboarding.module.css";

const PaymentsAcceptedOnboarding = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state?.user?.user);
  const providerId = user?.providerId;

  const [provider, setProvider] = useState(null);
  const [fetchedStates, setFetchedStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    if (!providerId) {
      setLoading(false);
      setError("Provider profile not found.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [providerRes, statesRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_BASE_URL}/specialist/provider/${providerId}`,
          user?.token
            ? { headers: { Authorization: `Bearer ${user.token}` } }
            : undefined,
        ),
        axios
          .get(`${process.env.REACT_APP_BASE_URL}/states/all`)
          .catch(() => null),
      ]);

      if (!providerRes.data?.success) {
        setError(providerRes.data?.message || "Failed to load payments.");
        return;
      }

      setProvider(providerRes.data.data);
      markSectionVisited("payments", providerId);

      const states = Array.isArray(statesRes?.data?.data)
        ? statesRes.data.data
        : Array.isArray(statesRes?.data)
          ? statesRes.data
          : [];
      const sorted = [...states].sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || "")),
      );
      setFetchedStates(sorted);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to load payments accepted information.",
      );
    } finally {
      setLoading(false);
    }
  }, [providerId, user?.token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <div className={classes.loading}>Loading payments accepted...</div>;
  }

  if (error || !provider) {
    return (
      <div className={classes.error}>
        {error || "Unable to load payments accepted."}
      </div>
    );
  }

  return (
    <div className={classes.page}>
      <div className={classes.header}>
        <button
          type="button"
          className={classes.backBtn}
          aria-label="Back to onboarding"
          onClick={() => navigate("/dashboard")}
        >
          <FaChevronLeft />
        </button>
        <div>
          <h1 className={classes.title}>Payments Accepted</h1>
          <p className={classes.subtitle}>
            Setup and view your accepted payment methods.
          </p>
        </div>
      </div>

      <div className={classes.content}>
        <ProviderPaymentsTab
          provider={provider}
          getProviderId={() => providerId}
          onProviderUpdated={(next) => setProvider(next)}
          fetchedStates={fetchedStates}
          readOnlyInsurance
          hideAccessCode
        />
      </div>
    </div>
  );
};

export default PaymentsAcceptedOnboarding;
